import { act } from 'react';
import { JSNumberToDayMapping } from '../../Constants';
import { getHighestCountTuples } from './WeekReviewHelper';

/**
 * Gets number of flows for each day in the week
 * @param {Array} flows Flows in week
 * @returns An object containting { 
 * activityLevels: <array> like [['Sun', 0], ['Mon', 0], ['Tues', 2]...for all 7 days of a week],
 * numActiveDays: <number> for the number of active days in the week
 * mostActiveDays: <array> The array of tuples for the most active days in the week [['Mon', 3], ['Fri', 3]]
 */
export function getActivityLevelsForWeek(flows) {
    if (!flows || flows.length === 0) {
        return null;
    }

    let flowsByDay = {
        Sun: 0,
        Mon: 0,
        Tue: 0,
        Wed: 0,
        Thu: 0,
        Fri: 0,
        Sat: 0
    }
    flows.forEach(flow => {
        const day = JSNumberToDayMapping[flow.date.getDay()].Day;
        flowsByDay[day]++;
    });

    const activityLevels = Object.entries(flowsByDay);
    const numActiveDays = activityLevels.filter(item => item[1] > 0)?.length;
    const mostActiveDays = getHighestCountTuples(activityLevels);

    return { activityLevels, numActiveDays, mostActiveDays };
}

/**
 * Calculates the most active times for a user based on their flow activity across distinct days.
 * @param {Array} flows - An array of the UI representation of flows
 * @returns {Array} An array of the top time overlap clusters, no more than 5.
 * - Each top cluster includes: { datetimes: Array<string|date>, rank: number, timeWindow: number, userFriendlyTimeWindow: string }
 */
export function getTopTimeOverlapClusters(flows) {
    if (!flows || flows.length === 0) {
        return null;
    }

    let topClusters = [];
    const numDistinctDaysFlowsHappened = getNumDistinctDaysFlowsHappened(flows);
    const timeWindows = [30, 60, 90, 120];

    flows.forEach(flow => {
        // for each flow, consider each time window
        timeWindows.forEach(window => {
            const cluster = getClusterInWindow(flow, flows, window, numDistinctDaysFlowsHappened);
            if (cluster) {
                topClusters = tryUpdateTopClusters(topClusters, cluster, numDistinctDaysFlowsHappened);
            }
        });
    });

    // update the top clusters with user friendly time window
    topClusters.forEach(topCluster => {
        const userFriendlyTimeWindow = getUserFriendlyTimeWindow(topCluster.datetimes);
        const timeEmoji = getTimeEmoji(userFriendlyTimeWindow);
        topCluster['userFriendlyTimeWindow'] = userFriendlyTimeWindow;
        topCluster['timeEmoji'] = timeEmoji;
    });

    return topClusters;
}

/**
 * @returns {number}  The total number of distinct dates flows happened.
 * E.g. for if 10 flows happened in a week between Mon-Fri, this should return 5.
 */
export function getNumDistinctDaysFlowsHappened(flows) {
    const distinctFlowDays = new Set();
    for (const flow of flows) {
        const date = typeof flow.date === 'string' ? new Date(flow.date) : flow.date;
        distinctFlowDays.add(`${date.getFullYear()}${date.getMonth()}${date.getDate()}`);
    }
    return distinctFlowDays.size;
}

/**
 * This function determines if any other flows fall within a given time window of the target flow.
 * For example: If target flow was taken December 4 at 4pm and the given time window is 30 minutes,
 * this function will check if any other flows on any other day also happen between 4 and 4:30pm.
 * @param {object} targetFlow 
 * @param {Array} flowsToCompare 
 * @param {number} window 
 * @param {number} numDistinctDaysFlowsHappened 
 * @returns {undefined|object} A cluster object that looks like:
 * { 
 *  datetimes: Array<string|date> An array of datetimes corresponding to other flows that fall within the time window as targetFlow
 *  rank: Number that is used to determine the relevance of a cluster compared to others
 *  timeWindow: Number representing duration in minutes
 * }
 * OR undefined if a cluster doesn't exist for the target flow in the given duration.
 */
export function getClusterInWindow(targetFlow, flowsToCompare, window, numDistinctDaysFlowsHappened) {
    const timeWindowWeights = { 30: 1.75, 60: 1.5, 90: 1.25, 120: 1 };
    // initialize with flow that'll be compared to all others
    let datetimesInCluster = [targetFlow.date];
    for (let comparedFlow of flowsToCompare) {
        if (areDatesSame(targetFlow.date, comparedFlow.date)) {
            // don't compare flows that are on the same day
            continue;
        }
        if (areTimesWithinDuration(targetFlow.date, comparedFlow.date, window)) {
            /* All datetimes in a cluster should represent distinct days. So if 2 flows on the same date are both in the 
            current timeWindow, then only one of them should be considered.
            */
            if (!datetimesInCluster.some((date) => areDatesSame(date, comparedFlow.date))) {
                datetimesInCluster.push(comparedFlow.date);
            }
        }
    }

    // There should be atleast 1 other flow that happened in the same time as target flow for it to be considered a cluster.
    if (datetimesInCluster.length < 2) {
        return;
    }

    const clusterRank = datetimesInCluster.length / numDistinctDaysFlowsHappened * timeWindowWeights[window];
    return {
        datetimes: datetimesInCluster, rank: clusterRank, timeWindow: window,
    }
}

/**
 * Returns true if the year/month/day parts of 2 Date objects are the same
 * @param {Date|string} date1 
 * @param {Date|string} date2 
 */
export function areDatesSame(date1, date2) {
    // In tests we have date strings coz they are easier to get, but in the actual execution they are date objects
    date1 = typeof date1 === 'string' ? new Date(date1) : date1;
    date2 = typeof date2 === 'string' ? new Date(date2) : date2;
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    );
}

/**
 * @param {Date|string} date1 
 * @param {Date|string} date2 
 * @param {number} duration - number of minutes 
 * @returns {boolean} True if the time portion of date2 happens no later than the given duration after date1's time.
 */
export function areTimesWithinDuration(date1, date2, duration) {
    const getMinutesSinceMidnight = (date) => {
        return date.getHours() * 60 + date.getMinutes();
    };
    date1 = typeof date1 === 'string' ? new Date(date1) : date1;
    date2 = typeof date2 === 'string' ? new Date(date2) : date2;

    const minutes1 = getMinutesSinceMidnight(date1);
    const minutes2 = getMinutesSinceMidnight(date2);

    const difference = minutes2 - minutes1;
    return difference > 0 && difference <= duration;
}

/**
 * Adds current cluster to top clusters if it is eligible to be in the top 5.
 * @param {Array} topClusters
 * @param {object} currentCluster - { datetimes: Array<string|date>, rank: number, timeWindow: number }
 * @param {number} numDistinctDaysFlowsHappened 
 * @returns {Array} top 5 clusters sorted by relevance
 */
export function tryUpdateTopClusters(topClusters, currentCluster, numDistinctDaysFlowsHappened) {
    const clusterDistinctDayCoverage = currentCluster.datetimes.length / numDistinctDaysFlowsHappened;
    if (clusterDistinctDayCoverage < 0.42) {
        // If a cluster doesn't have atleast 42% coverage of total distinct days flows happened, it is not considered to be relevant.
        // e.g. if flows happened on 7 different days, a cluster should have atleast 3 of those days for it to be relevant.
        // Therefore it will not be added to the top list.
        return topClusters;
    }

    const overlappingClusters = [currentCluster];
    // Check if the current cluster overlaps with any other cluster in the top array.
    for (let i = 0; i < topClusters.length; i++) {
        if (doClustersOverlap(topClusters[i], currentCluster)) {
            // remove cluster at i
            const overlappingCluster = topClusters.splice(i, 1)[0]; // splice returns the element in an array instead of just the removed element.
            overlappingClusters.push(overlappingCluster);
            i--;
        }
    }
    // This will sort the overlapping clusters passed in.
    sortClusters(overlappingClusters, numDistinctDaysFlowsHappened);
    // Push top choice from the overlapping clusters back into the top.
    topClusters.push(overlappingClusters[0]);
    sortClusters(topClusters, numDistinctDaysFlowsHappened);

    if (topClusters.length > 5) {
        topClusters.pop()
    }
    return topClusters;
}

/**
 * @param {Object} cluster1 
 * @param {Object} cluster2 
 * @returns {boolean} True if the datetime arrays in 2 clusters have any datetimes in common. 
 * Each flow has a unique timestamp so when clusters overlap it means they both are referring to atleast 1 same flow
 */
export function doClustersOverlap(cluster1, cluster2) {
    for (let date1 of cluster1.datetimes) {
        for (let date2 of cluster2.datetimes) {
            date1 = typeof date1 === 'string' ? new Date(date1) : date1;
            date2 = typeof date2 === 'string' ? new Date(date2) : date2;
            if (date1.getTime() === date2.getTime()) {
                return true; // Found a common date
            }
        }
    }
    return false; // No common dates found
}

/**
 * Sorts an array of clusters by rank. If two clusters have the same rank:
 * - The smaller cluster in tighter window is prioritized if its size is at least 70% of the total days, 
 *   or if the two clusters size difference is less than 16% of the numDistinctDaysFlowsHappened.
 * - Otherwise, the larger cluster is prioritized.
 * 
 * @param {Array<{rank: number, datetimes: Array<string|date>, timeWindow: number}>} clusters - The array of cluster objects to be sorted.
 * @param {number} numDistinctDaysFlowsHappened 
 * @returns No return, function sorts the input array in-place
 */
export function sortClusters(clusters, numDistinctDaysFlowsHappened) {
    clusters.sort((a, b) => {
        if (b.rank !== a.rank) {
            return b.rank - a.rank;
        }
        else if (
            Math.min(a.datetimes.length, b.datetimes.length) / numDistinctDaysFlowsHappened >= 0.7 ||
            Math.abs(a.datetimes.length - b.datetimes.length) < numDistinctDaysFlowsHappened * 0.16
        ) {
            // choose smaller one
            return a.datetimes.length - b.datetimes.length
        }
        else {
            // choose larger
            return b.datetimes.length - a.datetimes.length
        }
    })
}

/**
 * @param {Array} datetimes An array of date strings or objects 
 * @returns {string} A user friendly string for the time window represented by the datetimes 
 * like 4:30-6pm, or 11pm-1am, or around 12pm
 */
export function getUserFriendlyTimeWindow(datetimes) {
    if (!Array.isArray(datetimes) || datetimes.length === 0) {
        throw new Error("Input must be a non-empty array.");
    }

    // Convert all elements to Date objects in local time
    const dates = datetimes.map(item => {
        let date = typeof item === "string" ? new Date(item) : item;
        return date;
    });

    // Sort by the time of day (ignoring the date)
    dates.sort((a, b) => {
        const timeA = a.getHours() * 60 + a.getMinutes();
        const timeB = b.getHours() * 60 + b.getMinutes();
        return timeA - timeB;
    });
    // Get the earliest and latest times
    const startTime = roundTime(dates[0]);
    const endTime = roundTime(dates[dates.length - 1]);

    if (startTime === endTime) {
        return `~${startTime}`;
    }
    // if both times are am or pm
    if (startTime.slice(-2) === endTime.slice(-2)) {
        // e.g. return around 4-6pm instead of 4pm-6pm
        return `${startTime.slice(0, -2)} - ${endTime}`;
    }
    return `${startTime} - ${endTime}`;
}

/**
 * Rounds the time to the nearest half hour mark.
 * E.g anything between 12am < 12:15am becomes 12am.
 * 12:15pm will become 12:30pm. 12:45pm becomes 1pm
 * @param {Date} date A datetime object 
 * @returns {string}
 */
export function roundTime(date) {
    let hours = date.getHours();
    let minutes = date.getMinutes();

    // Round minutes
    if (minutes >= 45) {
        minutes = 0;
        hours += 1; // Increment hours if rounding up crosses the hour boundary
    }
    else if (minutes >= 15) {
        minutes = 30;
    }
    else {
        minutes = 0;
    }

    if (hours === 24) {
        hours = 0; // Adjust for midnight (24 => 0)
    }

    const period = hours >= 12 ? "pm" : "am";
    hours = hours % 12 || 12; // Convert to 12-hour format, making 0 => 12

    return minutes === 0 ? `${hours}${period}` : `${hours}:${minutes}${period}`;
}

const getTimeEmoji = (timeWindowStr) => {
    const clockEmojiMap = {
        "1": "🕐", "1:30": "🕜",
        "2": "🕑", "2:30": "🕝",
        "3": "🕒", "3:30": "🕞",
        "4": "🕓", "4:30": "🕟",
        "5": "🕔", "5:30": "🕠",
        "6": "🕕", "6:30": "🕡",
        "7": "🕖", "7:30": "🕢",
        "8": "🕗", "8:30": "🕣",
        "9": "🕘", "9:30": "🕤",
        "10": "🕙", "10:30": "🕥",
        "11": "🕚", "11:30": "🕦",
        "12": "🕛", "12:30": "🕧"
    };

    const match = timeWindowStr.match(/^~?(\d{1,2}(:\d{2})?)/);
    const startTime = match ? match[1] : null;

    return clockEmojiMap[startTime] ?? '';
} 
